import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> General, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/General.php`:
 *
 *   index                 -> GET  /settings/general           (fila)
 *   default_settings      -> GET  /settings/general/default_settings
 *   save_general_settings -> POST /settings/general/default_settings
 *
 * Atat, si atat are si portalul: filele Events, Slugs and default messages si
 * Custom messages au fost scoase de acolo la 26.9.8.5, si taiate si de aici.
 * De ce, si de ce nu se pun la loc, scrie in capul clasei `GeneralSettings`
 * (`v4/api/src/Settings/General/GeneralSettings.php`).
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * O salvare refuzata NU vine ca `status: 0` intr-un raspuns de 200, ca in
 * portal: vine ca 400 cu mesajul serverului, deci ajunge la ecran ca `ApiError`
 * si se arata asa cum e scris acolo.
 */

/**
 * O fila din bara.
 *
 * `group` e in plus fata de Inventoree: bara poarta un cap de sectiune, iar
 * impartirea o hotaraste serverul, care stie si ce drepturi are omul. Ecranul
 * doar deseneaza un titlu nou de fiecare data cand se schimba `group`, si nu
 * stie nimic despre ce grupuri exista - de aia a ramas neatins cand din patru
 * file a ramas una.
 */
export type GeneralTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  group: string
  title?: string
  external?: boolean
}

export type GeneralShell = {
  pageTitle: string
  moduleUrl: string
  /** DEJA filtrate pe server: aici vin numai filele pe care omul are voie sa le vada. */
  tabs: GeneralTab[]
}

/** O optiune de lista, asa cum o trimite serverul: valoarea si eticheta ei. */
export type GeneralOption = {
  value: string
  label: string
}

export const TABS = ['default_settings'] as const

export type GeneralTabId = (typeof TABS)[number]

export function isGeneralTab(value: string): value is GeneralTabId {
  return (TABS as readonly string[]).includes(value)
}

export function generalShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'general', 'shell'],
    queryFn: () => apiGet<GeneralShell>('/settings/general'),
  })
}

/**
 * Cele trei valori implicite ale furnizorului, plus listele din care se aleg -
 * scrise o singura data, pe server.
 *
 * `exists` spune daca furnizorul are deja un rand scris; fara el, valorile
 * implicite ar arata la fel ca niste valori alese cu mana.
 */
export type IdleLimit = { min: number; max: number; default: number }

export type GeneralDefaults = {
  language: string
  currency: string
  temperature: string
  /*
   * Cele doua ceasuri de securitate (cec-master 26.9.10.1): cat sta portalul
   * deschis fara atingere si plafonul sesiunii. Vin deja incadrate, cu
   * plafoanele lor in `idle_limits` - formularul le pune pe `min`/`max`.
   */
  idle_minutes: number
  session_hours: number
  idle_limits: { minutes: IdleLimit; hours: IdleLimit }
  exists: boolean
  languages: GeneralOption[]
  currencies: GeneralOption[]
  temperatures: GeneralOption[]
}

export function defaultSettingsQuery() {
  return queryOptions({
    queryKey: ['settings', 'general', 'default-settings'],
    queryFn: () => apiGet<GeneralDefaults>('/settings/general/default_settings'),
  })
}

export function saveDefaultSettings(fields: Record<string, PostValue>) {
  return apiPost<{
    saved: boolean
    language: string
    currency: string
    temperature: string
    idle_minutes: number
    session_hours: number
  }>(
    '/settings/general/default_settings',
    fields,
  )
}
